import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/apps/Avatar';
import { supabase } from '@/lib/supabase';
import { type AppNotification, loadAppNotifications, markAppNotificationsRead } from '@/lib/app-collaboration';
import { type AppMember, loadAppMembers } from '@/lib/app-records';

const LABELS: Record<AppNotification['kind'], string> = {
  mentioned: 'vous a mentionné',
  assigned: 'vous a assigné une ligne',
  commented: 'a commenté une ligne qui vous est assignée',
};

/** « Pour vous » in Aujourd'hui: unread mentions, assignments and comments on my objects. */
export const AppInbox = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [members, setMembers] = useState<AppMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    void loadAppNotifications()
      .then((list) => { setError(null); setItems(list.filter((item) => !item.readAt)); })
      .catch((cause) => { console.error('app_notifications_load_failed', cause); setError('Les notifications n’ont pas pu être chargées.'); });
  }, []);

  useEffect(() => {
    reload();
    // Without members, names fall back to « Quelqu’un »: the inbox stays usable.
    void loadAppMembers().then(setMembers).catch((cause) => console.warn('app_members_load_failed', cause));
    const channel = supabase.channel(`app-inbox-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'app_notifications' }, reload)
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [reload]);

  if (items.length === 0 && !error) return null;
  if (items.length === 0) return <p role="alert" className="mb-5 text-sm text-red-700">{error}</p>;
  const nameOf = (userId: string | null) => members.find((member) => member.userId === userId)?.name ?? 'Quelqu’un';
  const open = async (item: AppNotification) => {
    // Opening the object matters more than the read mark: log and go on.
    await markAppNotificationsRead([item.id]).catch((cause) => console.warn('app_notification_mark_read_failed', cause));
    navigate(`/apps/${encodeURIComponent(item.appKey)}${item.recordId ? `?record=${item.recordId}` : ''}`);
  };

  return (
    <section aria-labelledby="app-inbox-title" className="mb-5 rounded-2xl border border-domino-cardBorder bg-domino-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="app-inbox-title" className="text-sm font-semibold text-domino-ink">Pour vous <span className="font-normal text-domino-inkSoft">{items.length}</span></h2>
        <button type="button" onClick={() => void markAppNotificationsRead(null).then(reload).catch((cause) => { console.error('app_notifications_mark_all_failed', cause); setError('Les notifications n’ont pas pu être marquées comme lues.'); })} className="text-xs text-domino-aubergine hover:underline">Tout marquer comme lu</button>
      </div>
      {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
      <ul className="mt-2">
        {items.map((item) => (
          <li key={item.id} className="border-t border-domino-cardBorder first:border-t-0">
            <button type="button" onClick={() => void open(item)} className="flex w-full items-start gap-2 py-2 text-left hover:bg-domino-warm">
              <Avatar name={nameOf(item.actorUserId)} size="xs" className="mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-domino-ink"><span className="font-medium">{nameOf(item.actorUserId)}</span> {LABELS[item.kind]} · {item.appTitle}</span>
                {item.excerpt && <span className="mt-0.5 line-clamp-1 block text-xs text-domino-inkSoft">« {item.excerpt} »</span>}
              </span>
              <span className="shrink-0 text-xs text-domino-inkSoft">{new Date(item.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
